import React, { useState, useEffect } from "react";
import { BannerWithoutBytesDTO } from "Types/Admin/AdminBannerType";
import { Tooltip } from "@mantine/core";
import "drag-drop-touch";

interface BannerTableProps {
  banners: BannerWithoutBytesDTO[];
  onSelectBanner: (ids: number[] | null) => void;
  onDeleteBanner: (id: number) => void;
  onViewBanner: (id: number) => void;
  onEditBanner: (id: number) => void;
  onReorderBanners?: (draggedId: number, targetId: number) => void;
  selectedBanner: number[] | null;
  isLoading?: boolean;
  isError?: boolean;
}

export const BannerTable: React.FC<BannerTableProps> = ({
  banners,
  onSelectBanner,
  onDeleteBanner,
  onViewBanner,
  onEditBanner,
  onReorderBanners,
  selectedBanner,
  isLoading = false,
  isError = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedBanners, setSelectedBanners] = useState<number[]>([]);
  const [draggedBannerId, setDraggedBannerId] = useState<number | null>(null);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down" | null>(null);

  useEffect(() => {
    let scrollInterval: any;

    if (scrollDirection) {
      scrollInterval = setInterval(() => {
        const speed = 40; // High speed for responsive scrolling
        const scrollContainer = document.querySelector("main.overflow-y-auto");
        if (scrollContainer) {
          scrollContainer.scrollBy(0, scrollDirection === "up" ? -speed : speed);
        } else {
          window.scrollBy(0, scrollDirection === "up" ? -speed : speed);
        }
      }, 16);
    }

    return () => {
      if (scrollInterval) clearInterval(scrollInterval);
    };
  }, [scrollDirection]);

  useEffect(() => {
    const handleDocumentDragOver = (e: DragEvent) => {
      const threshold = 120; // Activation distance from edge
      const { clientY } = e;
      const viewportHeight = window.innerHeight;

      if (clientY < threshold) {
        setScrollDirection("up");
      } else if (clientY > viewportHeight - threshold) {
        setScrollDirection("down");
      } else {
        setScrollDirection(null);
      }
    };

    const handleDragEnd = () => {
      setScrollDirection(null);
      setDraggedBannerId(null);
    };

    if (draggedBannerId) {
      document.addEventListener("dragover", handleDocumentDragOver);
      document.addEventListener("dragend", handleDragEnd);
      document.addEventListener("drop", handleDragEnd);
    }

    return () => {
      document.removeEventListener("dragover", handleDocumentDragOver);
      document.removeEventListener("dragend", handleDragEnd);
      document.removeEventListener("drop", handleDragEnd);
    };
  }, [draggedBannerId]);

  const totalPages = Math.ceil(banners.length / itemsPerPage);
  const startItem =
    banners.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, banners.length);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const currentPageBannerIds = banners
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
        .map((banner) => banner.id);
      setSelectedBanners(currentPageBannerIds);

      if (currentPageBannerIds.length > 0) {
        onSelectBanner(currentPageBannerIds);
      }
    } else {
      setSelectedBanners([]);
      onSelectBanner(null);
    }
  };

  const handleSelectBanner = (id: number, checked: boolean) => {
    let newSelectedBanners: number[];

    if (checked) {
      newSelectedBanners = [...selectedBanners, id];
    } else {
      newSelectedBanners = selectedBanners.filter(
        (bannerId) => bannerId !== id,
      );
    }

    setSelectedBanners(newSelectedBanners);
    onSelectBanner(newSelectedBanners.length > 0 ? newSelectedBanners : null);

    const currentPageBannerIds = banners
      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
      .map((banner) => banner.id);

    setSelectAll(
      currentPageBannerIds.every((id) => newSelectedBanners.includes(id)),
    );
  };

  const isValidUrl = (url: string | null): boolean => {
    if (!url) return false;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const getDisplayableImage = (imagePath: string | null): string | null => {
    if (!imagePath) return null;

    if (isValidUrl(imagePath)) {
      return imagePath;
    }

    const filename = imagePath.split("\\").pop();
    if (filename) {
      return `https://ecommercetesting.objectstore.e2enetworks.net/banner/${filename}`;
    }

    return null;
  };

  return (
    <div className="font-body tracking-wider overflow-x-auto">
      {isError ? (
        <div className="py-10 text-center text-red-500">
          Error loading banners
        </div>
      ) : (
        <>
          <table className="min-w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="py-3 px-4 text-left left-0 bg-white">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  S.No.
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Title
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap hidden md:table-cell">
                  Description
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap hidden md:table-cell">
                  Button Text
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Desktop Image
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Mobile Image
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                  Status
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {banners.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    No banners found
                  </td>
                </tr>
              ) : (
                banners
                  .slice(
                    (currentPage - 1) * itemsPerPage,
                    currentPage * itemsPerPage,
                  )
                  .map((banner) => {
                    const desktopImage = getDisplayableImage(banner.bannerPath);
                    const mobileImage = getDisplayableImage(
                      banner.bannerPathTwo,
                    );

                    return (
                      <tr
                        key={banner.id}
                        draggable
                        onDragStart={(e) => {
                          setDraggedBannerId(banner.id);
                          e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          if (draggedBannerId && draggedBannerId !== banner.id && onReorderBanners) {
                            onReorderBanners(draggedBannerId, banner.id);
                          }
                          setDraggedBannerId(null);
                        }}
                        className={`border-t cursor-move hover:bg-gray-50 transition-colors ${selectedBanner?.includes(banner.id)
                          ? "bg-blue-50"
                          : ""
                          } ${draggedBannerId === banner.id ? "opacity-50" : ""}`}
                        onClick={() => {
                          const isSelected = selectedBanners.includes(
                            banner.id,
                          );
                          if (isSelected) {
                            handleSelectBanner(banner.id, false);
                          } else {
                            handleSelectBanner(banner.id, true);
                          }
                        }}
                      >
                        <td className="py-3 px-4 align-middle">
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={selectedBanners.includes(banner.id)}
                            onChange={(e) => {
                              e.stopPropagation();
                              handleSelectBanner(banner.id, e.target.checked);
                            }}
                          />
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-500 font-medium align-middle">
                          {banner.bannerLnNo}
                        </td>
                        <td
                          className="py-3 px-4 text-sm max-w-[150px] md:max-w-[180px] align-middle"
                        >
                          <Tooltip label={banner.title || "Untitled Banner"} multiline>
                            <div className="line-clamp-4 md:line-clamp-1 cursor-pointer">
                              {banner.title || "Untitled Banner"}
                            </div>
                          </Tooltip>
                        </td>
                        <td
                          className="py-3 px-4 text-sm hidden md:table-cell max-w-[250px] align-middle"
                        >
                          <Tooltip label={banner.bannerDescription || ""} multiline>
                            <div className="line-clamp-3 cursor-pointer">
                              {banner.bannerDescription || ""}
                            </div>
                          </Tooltip>
                        </td>
                        <td className="py-3 px-4 text-sm hidden md:table-cell align-middle">
                          {banner.buttonText || "-"}
                        </td>
                        <td className="py-3 px-4 align-middle">
                          {desktopImage ? (
                            <img
                              src={desktopImage}
                              alt="Desktop Banner"
                              className="h-10 object-contain"
                              width="auto"
                              height="auto"
                              onError={(e) => {
                                e.currentTarget.src = "/no-image.png";
                              }}
                            />
                          ) : (
                            <span className="text-gray-400">No image</span>
                          )}
                        </td>

                        <td className="py-3 px-4 align-middle">
                          {mobileImage ? (
                            <img
                              src={mobileImage}
                              alt="Mobile Banner"
                              className="h-10 object-contain"
                              onError={(e) => {
                                e.currentTarget.src = "/no-image.png";
                              }}
                            />
                          ) : (
                            <span className="text-gray-400">No image</span>
                          )}
                        </td>
                        <td className="py-3 px-4 align-middle">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              banner.isActive !== "N"
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {banner.isActive !== "N"
                              ? "Active"
                              : "Inactive"}
                          </span>
                        </td>
                        <td className="py-3 px-4 align-middle">
                          <div className="flex items-center gap-2">
                            <button
                              className="text-blue-500 hover:text-blue-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewBanner(banner.id);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-eye-icon lucide-eye"
                              >
                                <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            </button>

                            <button
                              className="text-green-500 hover:text-green-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                onEditBanner(banner.id);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-pencil-icon lucide-pencil"
                              >
                                <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                <path d="m15 5 4 4" />
                              </svg>
                            </button>

                            <button
                              className="text-red-500 hover:text-red-700"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteBanner(banner.id);
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="lucide lucide-trash2-icon lucide-trash-2"
                              >
                                <path d="M3 6h18" />
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                <line x1="10" x2="10" y1="11" y2="17" />
                                <line x1="14" x2="14" y1="11" y2="17" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>

          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="hidden md:flex">
              Showing {startItem} to {endItem} of {banners.length} entries
            </div>
            <div className="flex items-center gap-4">
              <select
                className="p-2 border rounded"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                {[5, 10, 15, 20].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  &lt;&lt;
                </button>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  &lt;
                </button>
                <span className="px-2 py-1 bg-gray-100 border rounded">
                  {currentPage}
                </span>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  &gt;
                </button>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={currentPage === totalPages || totalPages === 0}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  &gt;&gt;
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
